import React, { useEffect, useRef, useState } from 'react';
import { RotateCcw, Loader2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const SECTIONS = [
  { title: 'Intended Use', body: 'This product is designed solely for simulated racing experiences. It is not a substitute for real-world driving instruction or professional racing training.' },
  { title: 'Health & Safety', body: 'Stop immediately if you experience dizziness, nausea, blurred vision or discomfort. Do not use if you have a history of epilepsy, seizures or heart conditions without medical advice. Take regular breaks.' },
  { title: 'Operation', body: 'Do not modify, adjust or disassemble any rig hardware without staff supervision. No food or liquids near the rig.' },
  { title: 'Age', body: 'Recommended for users aged 16+. Children under 16 must be supervised by an adult at all times.' },
  { title: 'Liability', body: 'Motion Reality accepts no liability for injury, loss or damage arising from misuse or unsupervised use. Your statutory rights are protected under the Consumer Rights Act 2015.' },
  { title: 'Data & GDPR', body: 'Your details are handled in line with UK GDPR and the Data Protection Act 2018. Contact sales@motionreality.co.uk for any data requests.' },
];

export default function WalkInDisclaimer({ prefillName, onComplete }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasSigned, setHasSigned] = useState(false);
  const [emergencyName, setEmergencyName] = useState('');
  const [emergencyPhone, setEmergencyPhone] = useState('');
  const [guardian, setGuardian] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = '#fff';
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    return { x, y };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const { x, y } = pos(e); ctx.beginPath(); ctx.moveTo(x, y); };
  const draw = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const { x, y } = pos(e); ctx.lineTo(x, y); ctx.stroke(); if (!hasSigned) setHasSigned(true); };
  const end = () => { drawing.current = false; };
  const clear = () => { const c = canvasRef.current; c.getContext('2d').clearRect(0, 0, c.width, c.height); setHasSigned(false); };

  const handleContinue = async () => {
    setError('');
    if (!hasSigned) return setError('Please sign in the box above.');
    if (!emergencyName.trim()) return setError('Emergency contact name is required.');
    if (!emergencyPhone.trim()) return setError('Emergency contact phone is required.');
    setSaving(true);
    try {
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'walk-in-signature.png', { type: 'image/png' });
      const upload = await base44.integrations.Core.UploadFile({ file });
      if (!upload?.file_url) throw new Error('Upload failed');
      onComplete({
        signatureUrl: upload.file_url,
        emergencyName: emergencyName.trim(),
        emergencyPhone: emergencyPhone.trim(),
        signedAsGuardian: guardian,
      });
    } catch (e) {
      setError('Could not save your signature. Please try again.');
      setSaving(false);
    }
  };

  return (
    <div>
      <h2 className="text-white font-black text-3xl uppercase mb-2">Terms &amp; signature</h2>
      <p className="text-white/40 text-sm mb-6">Please read and sign to use the sim rig.</p>

      <div className="bg-[#111115] border border-white/10 p-5 space-y-3 text-sm text-white/60 max-h-48 overflow-y-auto mb-6">
        {SECTIONS.map((s, i) => (
          <div key={i}>
            <p className="text-white font-bold text-xs uppercase tracking-wider">{s.title}</p>
            <p className="mt-0.5">{s.body}</p>
          </div>
        ))}
      </div>

      <p className="text-white/40 text-xs font-bold tracking-widest uppercase mb-1">Signature</p>
      <div className="relative">
        <canvas
          ref={canvasRef}
          onMouseDown={start} onMouseMove={draw} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={draw} onTouchEnd={end}
          className="w-full h-32 bg-[#111115] border border-white/10 touch-none cursor-crosshair"
        />
        {!hasSigned && <span className="absolute inset-0 flex items-center justify-center text-white/25 text-sm pointer-events-none">Sign here</span>}
        <button type="button" onClick={clear} className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] text-white/50 bg-white/5 px-2 py-1">
          <RotateCcw className="w-3 h-3" /> Clear
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
        <label className="block">
          <span className="text-white/40 text-xs font-bold tracking-widest uppercase">Emergency contact name</span>
          <input value={emergencyName} onChange={(e) => setEmergencyName(e.target.value)} className="mt-1 w-full bg-[#111115] border border-white/10 focus:border-primary text-white text-lg px-4 py-3.5 outline-none" />
        </label>
        <label className="block">
          <span className="text-white/40 text-xs font-bold tracking-widest uppercase">Emergency contact phone</span>
          <input value={emergencyPhone} onChange={(e) => setEmergencyPhone(e.target.value)} type="tel" className="mt-1 w-full bg-[#111115] border border-white/10 focus:border-primary text-white text-lg px-4 py-3.5 outline-none" />
        </label>
      </div>

      <label className="flex items-center gap-2 text-white/60 text-xs mt-4">
        <input type="checkbox" checked={guardian} onChange={(e) => setGuardian(e.target.checked)} className="accent-primary w-4 h-4" />
        I am signing as a parent / guardian on behalf of a user under 16
      </label>

      {error && <p className="text-red-400 text-sm mt-4">{error}</p>}

      <button onClick={handleContinue} disabled={saving} className="mt-6 w-full bg-primary text-white font-black text-sm tracking-widest py-4 disabled:opacity-50 flex items-center justify-center gap-2">
        {saving && <Loader2 className="w-4 h-4 animate-spin" />} CONTINUE TO PAYMENT
      </button>
    </div>
  );
}